<script>
import {
    BCard,
    BCardTitle,
    BCol,
    BFormDatepicker,
    BFormGroup,
    BRow,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Overlay from '@core/components/common/Overlay.vue'
import { Button } from '@core/components/common'
import { LegislationApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import Actions from '@/views/administration/legislation/components/Actions.vue'
import TranslationTabs from '@/views/administration/legislation/components/TranslationTabs.vue'

let localQuestionKey = 0

function today() {
    return (new Date()).toISOString().substring(0, 10)
}

function dateOnly(value) {
    return value ? value.substring(0, 10) : null
}

function isoDate(value) {
    return value ? `${value.substring(0, 10)}T00:00:00+00:00` : null
}

function nextQuestionKey() {
    localQuestionKey += 1

    return localQuestionKey
}

export default {
    components: {
        Actions,
        BCard,
        BCardTitle,
        BCol,
        BFormDatepicker,
        BFormGroup,
        BRow,
        Button,
        Overlay,
        TranslationTabs,
        ValidationObserver,
        vSelect,
    },
    mixins: [backendResponse],
    props: {
        id: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            showLoading: true,
            scopeOptions: [
                { id: 'within_eu', title: 'within_eu' },
                { id: 'outside_eu', title: 'outside_eu' },
                { id: null, title: 'null' },
            ],
            groupTranslationFields: [
                {
                    key: 'title',
                    label: this.$t('Component(Label):name'),
                    placeholder: this.$t('Component(Placeholder):type-here'),
                    requiredIn: ['en'],
                },
                {
                    key: 'description',
                    label: this.$t('Component(Label):description'),
                    placeholder: this.$t('Component(Placeholder):description'),
                    type: 'textarea',
                },
            ],
            questionTranslationFields: [
                {
                    key: 'question',
                    label: this.$t('Component(Label):question'),
                    placeholder: this.$t('Component(Placeholder):type-here'),
                    requiredIn: ['en'],
                },
                {
                    key: 'description',
                    label: this.$t('Component(Label):description'),
                    placeholder: this.$t('Component(Placeholder):description'),
                    type: 'textarea',
                },
            ],
            questionGroup: this.emptyQuestionGroup(),
        }
    },
    computed: {
        pageTitle() {
            if (!this.id) {
                return 'Create new question group'
            }

            return `Edit ${this.enTitle()}`
        },
    },
    created() {
        if (!this.id) {
            this.showLoading = false

            return
        }

        LegislationApi
            .getQuestionGroup(this.id)
            .then(r => {
                this.questionGroup = this.normalizeQuestionGroup(r.data)
            })
            .catch(this.handleBackendError)
            .finally(() => {
                this.showLoading = false
            })
    },
    methods: {
        emptyQuestionGroup() {
            return {
                translations: {},
                scope: this.scopeOptions ? this.scopeOptions[0] : { id: 'within_eu', title: 'within_eu' },
                validFrom: today(),
                validTo: '2099-12-31',
                questions: [],
            }
        },
        normalizeQuestionGroup(data) {
            return {
                ...data,
                translations: data.translations || {},
                scope: this.scopeOption(data.scope),
                validFrom: dateOnly(data.validFrom),
                validTo: dateOnly(data.validTo),
                questions: (data.questions || []).map(question => this.normalizeQuestion(question)),
            }
        },
        normalizeQuestion(question) {
            return {
                ...question,
                localKey: nextQuestionKey(),
                translations: question.translations || {},
                type: 'boolean',
                validFrom: dateOnly(question.validFrom),
                validTo: dateOnly(question.validTo),
            }
        },
        emptyQuestion() {
            return {
                localKey: nextQuestionKey(),
                translations: {},
                type: 'boolean',
                validFrom: today(),
                validTo: '2099-12-31',
            }
        },
        scopeOption(scope) {
            const option = this.scopeOptions.find(scopeOption => scopeOption.id === scope)

            return option || this.scopeOptions[0]
        },
        addQuestion() {
            this.questionGroup.questions.push(this.emptyQuestion())
        },
        removeQuestion(index) {
            this.questionGroup.questions.splice(index, 1)
        },
        enTitle() {
            return this.questionGroup.translations
                && this.questionGroup.translations.en
                && this.questionGroup.translations.en.title
                ? this.questionGroup.translations.en.title
                : (this.questionGroup.title || '')
        },
        questionLabel(question) {
            return question.translations
                && question.translations.en
                && question.translations.en.question
                ? question.translations.en.question
                : 'Question'
        },
        close() {
            this.$router.push({ name: 'administration-legislation-question-groups' })
        },
        save() {
            this.$refs.questionGroup.validate().then(isValid => {
                if (!isValid || !this.validateDates()) {
                    return
                }

                this.showLoading = true
                const request = this.id
                    ? LegislationApi.updateQuestionGroup(this.id, this.gatherSaveAttributes())
                    : LegislationApi.createQuestionGroup(this.gatherSaveAttributes())

                request
                    .then(() => {
                        this.showDefaultSuccessMessage('Question group has been saved')
                        this.$router.push({ name: 'administration-legislation-question-groups' })
                    })
                    .catch(this.handleBackendError)
                    .finally(() => {
                        this.showLoading = false
                    })
            })
        },
        validateDates() {
            const violations = []

            if (this.questionGroup.validFrom && this.questionGroup.validTo && this.questionGroup.validFrom > this.questionGroup.validTo) {
                violations.push({ message: 'Group valid from must be before valid to' })
            }

            this.questionGroup.questions.forEach((question, index) => {
                if (question.validFrom && question.validTo && question.validFrom > question.validTo) {
                    violations.push({ message: `Question #${index + 1} valid from must be before valid to` })
                }
            })

            if (violations.length) {
                this.showViolationErrors(violations)

                return false
            }

            return true
        },
        gatherSaveAttributes() {
            return {
                translations: this.questionGroup.translations,
                scope: this.questionGroup.scope ? this.questionGroup.scope.id : null,
                validFrom: isoDate(this.questionGroup.validFrom),
                validTo: isoDate(this.questionGroup.validTo),
                questions: this.questionGroup.questions.map(question => {
                    const prepared = {
                        translations: question.translations,
                        type: 'boolean',
                        validFrom: isoDate(question.validFrom),
                        validTo: isoDate(question.validTo),
                    }

                    if (question.id) {
                        prepared.id = question.id
                    }

                    return prepared
                }),
            }
        },
    },
}
</script>

<template>
    <Overlay :show="showLoading">
        <validation-observer ref="questionGroup">
            <b-row class="pb-2">
                <b-col>
                    <h2 class="float-left pr-1 mb-1">
                        {{ pageTitle }}
                    </h2>
                    <Actions
                        class="float-right"
                        @close="close"
                        @save="save"
                    />
                </b-col>
            </b-row>

            <b-card no-body>
                <div class="card-header pb-1">
                    <b-card-title>
                        {{ $t('Component(Text):general-info') }}
                    </b-card-title>
                </div>
                <div class="pl-2 pr-2 pb-1">
                    <TranslationTabs
                        v-model="questionGroup.translations"
                        :fields="groupTranslationFields"
                        :default-values="{title: '', description: null}"
                    />

                    <b-row class="mt-1">
                        <b-col md="4">
                            <b-form-group
                                :label="$t('Component(Label):scope')"
                                label-for="question-group-scope"
                            >
                                <v-select
                                    id="question-group-scope"
                                    v-model="questionGroup.scope"
                                    :options="scopeOptions"
                                    label="title"
                                    :clearable="false"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group
                                :label="$t('Component(Label):valid-from')"
                                label-for="question-group-valid-from"
                            >
                                <b-form-datepicker
                                    id="question-group-valid-from"
                                    v-model="questionGroup.validFrom"
                                    :date-format-options="$store.state.component.datePicker.format"
                                    :locale="$store.state.component.datePicker.locale"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group
                                :label="$t('Component(Label):valid-to')"
                                label-for="question-group-valid-to"
                            >
                                <b-form-datepicker
                                    id="question-group-valid-to"
                                    v-model="questionGroup.validTo"
                                    :date-format-options="$store.state.component.datePicker.format"
                                    :locale="$store.state.component.datePicker.locale"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
            </b-card>

            <b-card no-body>
                <div class="card-header pb-1">
                    <b-card-title>
                        Question Groups
                    </b-card-title>
                    <Button
                        class="ml-auto mt-0 btn-icon"
                        @click="addQuestion"
                    >
                        <feather-icon icon="PlusIcon" />
                    </Button>
                </div>
                <div class="pl-2 pr-2 pb-1">
                    <b-card
                        v-for="(question, index) in questionGroup.questions"
                        :key="question.localKey"
                        no-body
                        border-variant="light"
                        class="mb-1"
                    >
                        <div class="card-header pb-1">
                            <b-card-title>
                                {{ questionLabel(question) }}
                            </b-card-title>
                            <Button
                                class="ml-auto mt-0 btn-icon"
                                variant="outline-danger"
                                @click="removeQuestion(index)"
                            >
                                <feather-icon icon="Trash2Icon" />
                            </Button>
                        </div>
                        <div class="pl-2 pr-2 pb-1">
                            <TranslationTabs
                                v-model="question.translations"
                                :fields="questionTranslationFields"
                                :default-values="{question: '', description: null}"
                            />
                            <b-row class="mt-1">
                                <b-col md="6">
                                    <b-form-group
                                        :label="$t('Component(Label):valid-from')"
                                        :label-for="'question-valid-from-' + question.localKey"
                                    >
                                        <b-form-datepicker
                                            :id="'question-valid-from-' + question.localKey"
                                            v-model="question.validFrom"
                                            :date-format-options="$store.state.component.datePicker.format"
                                            :locale="$store.state.component.datePicker.locale"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group
                                        :label="$t('Component(Label):valid-to')"
                                        :label-for="'question-valid-to-' + question.localKey"
                                    >
                                        <b-form-datepicker
                                            :id="'question-valid-to-' + question.localKey"
                                            v-model="question.validTo"
                                            :date-format-options="$store.state.component.datePicker.format"
                                            :locale="$store.state.component.datePicker.locale"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-card>
                </div>
            </b-card>
        </validation-observer>
    </Overlay>
</template>
